const BUNDLE_SECTION_ID = 'kl-bundle-products';
const bundleItems = new Map();

function getBundleTotals(items, discountPercent) {
  let originalTotal = 0;
  let itemCount = 0;

  items.forEach((item) => {
    originalTotal += item.price * item.quantity;
    itemCount += item.quantity;
  });

  const safePercent = discountPercent > 0 && discountPercent < 100 ? discountPercent : 0;
  const savings = Math.round((originalTotal * safePercent) / 100);

  return { originalTotal, discountedTotal: originalTotal - savings, savings, itemCount };
}

function getCheckoutPath(items, cartUrl, discountCode) {
  const lines = [];

  items.forEach((item) => {
    lines.push(`${item.variantId}:${item.quantity}`);
  });

  if (!lines.length) return '';

  const path = `${cartUrl}/${lines.join(',')}`;

  return discountCode ? `${path}?discount=${encodeURIComponent(discountCode)}` : path;
}

function formatMoney(amountInCents, currencyCode) {
  try {
    const hasCents = amountInCents % 100 !== 0;
    const formatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode || 'USD',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2,
    });

    return formatter.format(amountInCents / 100);
  } catch (error) {
    console.debug('Bundle builder could not format a price', { currencyCode, error });
    return '';
  }
}

function getVisibleFace(cardElement) {
  return cardElement.querySelector('[data-kl-product-card-face]:not([hidden])');
}

function getRootElement() {
  return document.querySelector('[data-kl-bundle]');
}

function buildRowElement(templateElement, item, currencyCode) {
  const rowElement = templateElement.content.firstElementChild.cloneNode(true);
  const imageElement = rowElement.querySelector('[data-kl-bundle-row-image]');
  const titleElement = rowElement.querySelector('[data-kl-bundle-row-title]');
  const priceElement = rowElement.querySelector('[data-kl-bundle-row-price]');
  const quantityElement = rowElement.querySelector('[data-kl-bundle-row-quantity]');
  const decreaseElement = rowElement.querySelector('[data-kl-bundle-decrease]');

  rowElement.dataset.klBundleRow = item.variantId;

  if (decreaseElement) decreaseElement.disabled = item.quantity <= 1;

  if (imageElement && item.image) {
    imageElement.src = item.image;
    imageElement.alt = '';
  }

  if (titleElement) titleElement.textContent = item.title;
  if (priceElement) priceElement.textContent = formatMoney(item.price, currencyCode);
  if (quantityElement) quantityElement.textContent = String(item.quantity);

  return rowElement;
}

function renderPanel(rootElement) {
  const panelElement = rootElement.querySelector('[data-kl-bundle-panel]');
  const listElement = rootElement.querySelector('[data-kl-bundle-list]');
  const templateElement = rootElement.querySelector('[data-kl-bundle-row-template]');
  const totalElement = rootElement.querySelector('[data-kl-bundle-total]');
  const wasElement = rootElement.querySelector('[data-kl-bundle-was]');
  const savingsElement = rootElement.querySelector('[data-kl-bundle-savings]');
  const emptyElement = rootElement.querySelector('[data-kl-bundle-empty]');
  const checkoutElement = rootElement.querySelector('[data-kl-bundle-checkout]');

  if (!panelElement || !listElement || !templateElement) return;

  const currencyCode = rootElement.dataset.klBundleCurrency;
  const discountPercent = Number.parseFloat(rootElement.dataset.klBundleDiscountPercent) || 0;
  const totals = getBundleTotals(bundleItems, discountPercent);
  const isEmpty = totals.itemCount === 0;

  listElement.replaceChildren();
  bundleItems.forEach((item) => {
    listElement.appendChild(buildRowElement(templateElement, item, currencyCode));
  });

  panelElement.classList.toggle('is-empty', isEmpty);
  if (emptyElement) emptyElement.hidden = !isEmpty;
  if (checkoutElement) checkoutElement.disabled = isEmpty;

  if (totalElement) {
    totalElement.textContent = isEmpty ? '' : formatMoney(totals.discountedTotal, currencyCode);
  }

  if (wasElement) {
    const hasSaving = !isEmpty && totals.savings > 0;

    wasElement.textContent = hasSaving ? formatMoney(totals.originalTotal, currencyCode) : '';
    wasElement.hidden = !hasSaving;
  }

  if (savingsElement) {
    const hasSaving = !isEmpty && totals.savings > 0;
    const savingsTemplate = savingsElement.dataset.klBundleSavingsTemplate || '';

    savingsElement.hidden = !hasSaving;
    if (hasSaving) {
      savingsElement.textContent = savingsTemplate.replace(
        '[amount]',
        formatMoney(totals.savings, currencyCode),
      );
    }
  }

  syncAddButtons(rootElement);
}

function isFaceAvailable(faceElement) {
  return faceElement.dataset.klProductCardAvailable !== 'false';
}

function getAddButtonText(buttonElement, isSoldOut, isAdded) {
  if (isSoldOut) return buttonElement.dataset.klBundleSoldOutText || '';
  if (isAdded) return buttonElement.dataset.klBundleAddedText || '';

  return buttonElement.dataset.klBundleAddText || '';
}

function syncAddButtons(rootElement) {
  rootElement.querySelectorAll('[data-kl-bundle-card]').forEach((cardElement) => {
    const buttonElement = cardElement.querySelector('[data-kl-bundle-add]');
    const faceElement = getVisibleFace(cardElement);

    if (!buttonElement || !faceElement) return;

    const isSoldOut = !isFaceAvailable(faceElement);
    const isAdded = !isSoldOut && bundleItems.has(faceElement.dataset.klProductCardVariant);
    const nextText = getAddButtonText(buttonElement, isSoldOut, isAdded);

    buttonElement.disabled = isSoldOut;
    buttonElement.setAttribute('aria-pressed', isAdded ? 'true' : 'false');

    if (buttonElement.textContent.trim() !== nextText) buttonElement.textContent = nextText;
  });
}

function addFaceToBundle(faceElement) {
  const variantId = faceElement.dataset.klProductCardVariant;

  if (!isFaceAvailable(faceElement)) {
    console.debug('Bundle builder: the visible product is sold out', {
      productId: faceElement.dataset.klProductCardFace,
    });
    return;
  }

  if (!variantId) {
    console.debug('Bundle builder: the visible product has no purchasable variant', {
      productId: faceElement.dataset.klProductCardFace,
    });
    return;
  }

  const existingItem = bundleItems.get(variantId);

  if (existingItem) {
    existingItem.quantity += 1;
    return;
  }

  bundleItems.set(variantId, {
    variantId,
    title: faceElement.dataset.klProductCardTitle || '',
    image: faceElement.dataset.klProductCardImage || '',
    price: Number.parseInt(faceElement.dataset.klProductCardPrice, 10) || 0,
    quantity: 1,
  });
}

function setRowQuantity(variantId, change) {
  const item = bundleItems.get(variantId);

  if (!item) return;

  const nextQuantity = item.quantity + change;

  if (nextQuantity < 1) return;

  item.quantity = nextQuantity;
}

function showTab(rootElement, tabIndex) {
  rootElement.querySelectorAll('[data-kl-bundle-tab]').forEach((tabElement) => {
    const isCurrent = tabElement.dataset.klBundleTab === tabIndex;

    tabElement.classList.toggle('is-active', isCurrent);
    tabElement.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
  });

  rootElement.querySelectorAll('[data-kl-bundle-tab-panel]').forEach((panelElement) => {
    panelElement.hidden = panelElement.dataset.klBundleTabPanel !== tabIndex;
  });

  rootElement.querySelectorAll('[data-kl-bundle-steps-panel]').forEach((trackElement) => {
    trackElement.hidden = trackElement.dataset.klBundleStepsPanel !== tabIndex;
  });

  const currentStep = rootElement.querySelector(
    `[data-kl-bundle-step^="${tabIndex}-"][aria-current="step"]`,
  );

  if (currentStep) loadStepPanel(rootElement, currentStep.dataset.klBundleStep);
}

function showStep(rootElement, stepKey) {
  const tabIndex = stepKey.split('-')[0];

  rootElement.querySelectorAll(`[data-kl-bundle-step^="${tabIndex}-"]`).forEach((stepElement) => {
    const isCurrent = stepElement.dataset.klBundleStep === stepKey;

    stepElement.classList.toggle('is-active', isCurrent);
    stepElement.setAttribute('aria-current', isCurrent ? 'step' : 'false');
  });

  rootElement
    .querySelectorAll(`[data-kl-bundle-step-panel^="${tabIndex}-"]`)
    .forEach((panelElement) => {
      panelElement.hidden = panelElement.dataset.klBundleStepPanel !== stepKey;
    });

  updateStepArrows(rootElement, tabIndex);
  loadStepPanel(rootElement, stepKey);
}

function updateStepArrows(rootElement, tabIndex) {
  const stepElements = rootElement.querySelectorAll(`[data-kl-bundle-step^="${tabIndex}-"]`);
  const previousElement = rootElement.querySelector(`[data-kl-bundle-previous="${tabIndex}"]`);
  const nextElement = rootElement.querySelector(`[data-kl-bundle-next="${tabIndex}"]`);
  const currentIndex = getCurrentStepIndex(rootElement, tabIndex);

  if (previousElement) previousElement.disabled = currentIndex <= 0;
  if (nextElement) nextElement.disabled = currentIndex >= stepElements.length - 1;
}

function getCurrentStepIndex(rootElement, tabIndex) {
  const currentElement = rootElement.querySelector(
    `[data-kl-bundle-step^="${tabIndex}-"][aria-current="step"]`,
  );

  if (!currentElement) return 0;

  return Number.parseInt(currentElement.dataset.klBundleStep.split('-')[1], 10) || 0;
}

function moveStep(rootElement, tabIndex, change) {
  const nextIndex = getCurrentStepIndex(rootElement, tabIndex) + change;

  if (nextIndex < 0) return;

  const nextElement = rootElement.querySelector(
    `[data-kl-bundle-step="${tabIndex}-${nextIndex}"]`,
  );

  if (nextElement) showStep(rootElement, nextElement.dataset.klBundleStep);
}

async function fetchBatch(collectionUrl, pageNumber) {
  const response = await fetch(
    `${collectionUrl}?section_id=${BUNDLE_SECTION_ID}&page=${pageNumber}`,
  );

  if (!response.ok) throw new Error(`Collection responded ${response.status}`);

  const markup = await response.text();

  return new DOMParser().parseFromString(markup, 'text/html');
}

function appendBatch(wrapElement, batchDocument, pageNumber) {
  const gridElement = wrapElement.querySelector('[data-kl-bundle-grid]');
  const moreElement = wrapElement.querySelector('[data-kl-bundle-more]');
  const pageInfoElement = batchDocument.querySelector('[data-kl-bundle-page-info]');
  const cardElements = batchDocument.querySelectorAll('[data-kl-bundle-card]');

  if (!gridElement) return;

  cardElements.forEach((cardElement) => gridElement.appendChild(cardElement));

  if (!moreElement) return;

  const hasNext = pageInfoElement && pageInfoElement.dataset.klBundleHasNext === 'true';

  moreElement.hidden = !hasNext;
  moreElement.dataset.klBundleNextPage = String(pageNumber + 1);
}

async function loadStepPanel(rootElement, stepKey) {
  const panelElement = rootElement.querySelector(`[data-kl-bundle-step-panel="${stepKey}"]`);

  if (!panelElement) return;

  const wrapElement = panelElement.querySelector('[data-kl-bundle-grid-wrap]');

  if (!wrapElement) return;
  if (wrapElement.dataset.klBundleLoaded !== 'false') return;

  const collectionUrl = wrapElement.dataset.klBundleCollectionUrl;

  if (!collectionUrl) return;

  wrapElement.dataset.klBundleLoaded = 'true';
  wrapElement.classList.add('is-loading');

  try {
    appendBatch(wrapElement, await fetchBatch(collectionUrl, 1), 1);
    syncAddButtons(rootElement);
  } catch (error) {
    wrapElement.dataset.klBundleLoaded = 'false';
    console.debug('Bundle builder could not load a step', { collectionUrl, error });
  } finally {
    wrapElement.classList.remove('is-loading');
  }
}

async function loadMore(rootElement, moreElement) {
  const wrapElement = moreElement.closest('[data-kl-bundle-grid-wrap]');

  if (!wrapElement) return;

  const collectionUrl = wrapElement.dataset.klBundleCollectionUrl;
  const pageNumber = Number.parseInt(moreElement.dataset.klBundleNextPage, 10) || 2;

  if (!collectionUrl) return;

  moreElement.disabled = true;

  try {
    appendBatch(wrapElement, await fetchBatch(collectionUrl, pageNumber), pageNumber);
    syncAddButtons(rootElement);
  } catch (error) {
    console.debug('Bundle builder could not load more products', { collectionUrl, pageNumber, error });
  } finally {
    moreElement.disabled = false;
  }
}

function handleDocumentClick(event) {
  if (!event.target || typeof event.target.closest !== 'function') return;

  const rootElement = getRootElement();

  if (!rootElement) return;

  const addElement = event.target.closest('[data-kl-bundle-add]');

  if (addElement) {
    const cardElement = addElement.closest('[data-kl-bundle-card]');
    const faceElement = cardElement && getVisibleFace(cardElement);

    if (faceElement) {
      addFaceToBundle(faceElement);
      renderPanel(rootElement);
    }
    return;
  }

  const removeElement = event.target.closest('[data-kl-bundle-remove]');

  if (removeElement) {
    const rowElement = removeElement.closest('[data-kl-bundle-row]');

    if (rowElement) {
      bundleItems.delete(rowElement.dataset.klBundleRow);
      renderPanel(rootElement);
    }
    return;
  }

  const stepperElement = event.target.closest('[data-kl-bundle-increase], [data-kl-bundle-decrease]');

  if (stepperElement) {
    const rowElement = stepperElement.closest('[data-kl-bundle-row]');
    const isIncrease = stepperElement.hasAttribute('data-kl-bundle-increase');

    if (rowElement) {
      setRowQuantity(rowElement.dataset.klBundleRow, isIncrease ? 1 : -1);
      renderPanel(rootElement);
    }
    return;
  }

  const tabElement = event.target.closest('[data-kl-bundle-tab]');

  if (tabElement) {
    showTab(rootElement, tabElement.dataset.klBundleTab);
    return;
  }

  const stepElement = event.target.closest('[data-kl-bundle-step]');

  if (stepElement) {
    showStep(rootElement, stepElement.dataset.klBundleStep);
    return;
  }

  const previousElement = event.target.closest('[data-kl-bundle-previous]');

  if (previousElement) {
    moveStep(rootElement, previousElement.dataset.klBundlePrevious, -1);
    return;
  }

  const nextElement = event.target.closest('[data-kl-bundle-next]');

  if (nextElement) {
    moveStep(rootElement, nextElement.dataset.klBundleNext, 1);
    return;
  }

  const moreElement = event.target.closest('[data-kl-bundle-more]');

  if (moreElement) {
    loadMore(rootElement, moreElement);
    return;
  }

  const toggleElement = event.target.closest('[data-kl-bundle-toggle]');

  if (toggleElement) {
    const panelElement = rootElement.querySelector('[data-kl-bundle-panel]');
    const isOpen = toggleElement.getAttribute('aria-expanded') === 'true';

    toggleElement.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    if (panelElement) panelElement.classList.toggle('is-open', !isOpen);
    return;
  }

  const checkoutElement = event.target.closest('[data-kl-bundle-checkout]');

  if (checkoutElement) {
    const checkoutPath = getCheckoutPath(
      bundleItems,
      rootElement.dataset.klBundleCartUrl,
      rootElement.dataset.klBundleDiscountCode,
    );

    if (checkoutPath) window.location.assign(checkoutPath);
  }
}

function observeCardFaces(rootElement) {
  new MutationObserver(() => syncAddButtons(rootElement)).observe(rootElement, {
    attributes: true,
    attributeFilter: ['hidden'],
    subtree: true,
  });
}

function initBundleBuilder() {
  const rootElement = getRootElement();

  if (!rootElement) return;

  renderPanel(rootElement);
  observeCardFaces(rootElement);
}

document.addEventListener('click', handleDocumentClick);

initBundleBuilder();

document.addEventListener('shopify:section:load', () => initBundleBuilder());
